/**
 * Scene duration: moved to Shared (spec 006) so the playback keyframe
 * resolver can use it; re-exported here for the editor's existing imports.
 */
import { calculateSceneDuration } from '../../../Shared/src/utils/sceneDuration';
import type { Keyframe, KeyframeTrack, Story } from '../../../Shared/src/@types';
import { toPlayableScene, type PlayableSceneInput } from '../../../Shared/src/runtime/playableScene';
import { MIN_SCENE_LENGTH_MS } from '../../../Shared/src/utils/sceneTiming';

export { calculateSceneDuration };

/** Room past the content the timeline always shows (spec 019). */
export const TIMELINE_PADDING_MS = 5000;
/** The shortest timeline (spec 019). */
export const TIMELINE_MIN_LENGTH_MS = 10000;

/**
 * The timeline's length (spec 019): the larger of the scene duration plus
 * 5 s, the time the panel's width shows (`viewportMs`), and 10 s, and never
 * shorter than the scene's play length (`playLengthMs`, `previewDurationMs`;
 * spec 017), so the ruler scrubs over the whole scene the learner sees. The
 * playhead, frame steps and keyframe moves clamp to [0, length].
 */
export function timelineLengthMs(
  scene: Pick<Story, 'dialogs' | 'sounds' | 'exitTransition'> | null,
  viewportMs = 0,
  playLengthMs = 0,
): number {
  return Math.max(calculateSceneDuration(scene) + TIMELINE_PADDING_MS, viewportMs, TIMELINE_MIN_LENGTH_MS, playLengthMs);
}

/**
 * The scene's play length as the player plays it (spec 012's
 * `resolveSceneLength` on `toPlayableScene`): the longer of the authored
 * duration and the content end, at least 1 s. Previews, the choice reveal
 * and the sound schedule all use it; there is no 10 s editor minimum
 * (spec 009). Pass the store's authored keyframes so keyframed motion
 * counts; memoised per scene object and keyframes.
 */
export function previewDurationMs(
  scene: (Partial<Story> & { duration?: number; keyframes?: KeyframeTrack[] }) | null,
  keyframes?: Map<string, Keyframe[]> | KeyframeTrack[],
): number {
  if (!scene) return MIN_SCENE_LENGTH_MS;
  const cached = lengthCache.get(scene);
  if (cached && cached.keyframes === keyframes) return cached.length;
  const tracks = keyframes instanceof Map
    ? Array.from(keyframes, ([trackId, kfs]) => ({ trackId, keyframes: kfs })).filter((t) => t.keyframes.length > 0)
    : keyframes ?? scene.keyframes;
  const length = toPlayableScene({ ...(scene as PlayableSceneInput), id: scene.id ?? 0, keyframes: tracks }).duration;
  lengthCache.set(scene, { keyframes, length });
  return length;
}

const lengthCache = new WeakMap<object, { keyframes: unknown; length: number }>();
